import {
  type CloudAccountChangeParams,
  CloudAccountChangeV2,
} from '@labrav/common'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStyles } from './MarketplaceEmptyStateMappingManager.styles'
import EmptyStateImage from '../../../common/images/Empty State.png'
import {
  ButtonV2,
  ModalConfirmV2,
  ModalSizeVariants,
  RadioButtonGroupMapping,
  RadioButtonsV2,
  RadioButtonType,
  TextInputV4,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { useDispatch, useSelector } from '../../../store'
import {
  createMarketplaceReferralTemplate,
  createMarketplaceSalesforceTemplate,
  getMarketplaceProductsWithDimensions,
} from '../../../modules/marketplaceMappingManager/action'
import { useUserType } from '@labrav/common'
import { customerFormData } from '../utils/customerFormData'
import { isEmpty } from 'lodash'
import { useNavigate, useParams } from 'react-router-dom'
import { updateAppAlert } from '@labrav/common'
import {
  extractAccountIdFromCombinedId,
  getSelectedAccountIdWithCloud,
  useAccountId,
  useCloudAccounts,
} from '@labrav/common'
import { useCloud } from '@labrav/common'
import { useFlagValue } from '@labrav/flags'
import { CRMTypes } from '../../../utils/constants'
import { getCloudAccountsForMappingManager } from '../utils/getCloudAccountsForMappingManager'
import { CloudType } from '../../../types/external'
import { FlyoutFunction } from '../../../modules/productListingFilter/types'
import { Typography } from '@mui/material'
import { actionTypeWrapper } from '@labrav/common'
import {
  clearProductsListing,
  getProducts,
  setSelectedProductOnListing,
} from '../../../modules/productsListing/action'
import { ProductsWithDimensions } from '../../../modules/marketplaceMappingManager/types'
import { CloudMarketplace } from '../../../modules/productsListing/types'
import { setSelectedAccount } from '@labrav/common'
import { setCloudMarketplaceProductListing } from '../../../modules/productListingFilter/action'
import {
  stopLoading,
  LoadingTypes,
  isLoading as Loading,
  useCurrentCloudProvider,
  SelectedCloudIcon,
} from '@labrav/common'

export const MarketplaceEmptyStateMappingManager: React.FC = () => {
  const { classes } = useStyles()
  const customerData = customerFormData()
  const { crmType } = customerData
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const { cloudType: routeCloudType, accountId: routeAccountId } = useParams<{
    cloudType?: string
    accountId?: string
  }>()

  const resolvedCloud = (
    routeCloudType || selectedCloudTypeProductListing
  ).toUpperCase() as CloudType
  const selectedAccountId = useAccountId({
    product: 'flyout',
    cloud: resolvedCloud,
  })
  const accountId = routeAccountId || selectedAccountId
  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    accountId || '',
    'flyout',
    resolvedCloud
  )

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
      const { cloud } = params
      const cloudPath = cloud.toLowerCase()

      const pathname = `/settings/${cloudPath}/${originalAccountId}/mappings/marketplace`
      navigate(pathname, { replace: true })

      await dispatch(stopLoading(LoadingTypes.GENERAL))
      await dispatch(
        setSelectedAccount(originalAccountId, 'flyout', cloud as CloudType)
      )
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
      await dispatch(actionTypeWrapper(cloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(cloud))

      await dispatch(getProducts(originalAccountId, cloud))
      await dispatch(stopLoading(LoadingTypes.GENERAL))
    },
    [dispatch, navigate, selectedCloudTypeProductListing, location.search]
  )
  return (
    <div
      className={classes.root}
      data-testid="marketplace-mapping-manager-empty-state-container"
    >
      <img
        src={EmptyStateImage}
        alt="Marketplace template icon"
        className={classes.templateIcon}
        data-testid="empty-template-icon"
      />
      <div className={classes.textContainer} data-testid="empty-state-text">
        <div className={classes.heading}>Create mapping templates</div>
        <div className={classes.subHeading}>
          Start mapping {crmType} fields to offer fields to auto populate the
          form. Create multiple templates for different offer types and save
          time all the while.
        </div>
        <div className={classes.btn}>
          <MarketplaceCreateTemplate
            handleCloudAccountChange={handleCloudAccountChange}
            selectedAccountIdWithCloud={selectedAccountIdWithCloud}
          />
        </div>
      </div>
    </div>
  )
}

export const MarketplaceCreateTemplate: React.FC<{
  buttonDisable?: boolean
  selectedAccountIdWithCloud?: string
  handleCloudAccountChange?: (params: CloudAccountChangeParams) => void
  handleProductChange?: (productId: string) => void
}> = ({
  buttonDisable = false,
  selectedAccountIdWithCloud,
  handleCloudAccountChange,
  handleProductChange,
}) => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const partnerType = useUserType()
  const customerData = customerFormData()
  const { crmType } = customerData
  const { value: isSalesforceMappingManagerEnabled } = useFlagValue(
    'mappingManagerMultipleApnSalesforce'
  )
  const { value: isAzureMarketplaceMappingManagerEnabled } = useFlagValue(
    'azureMarketplaceMappingManager'
  )
  const { value: isAzureHubspotMappingManagerEnabled } = useFlagValue(
    'azureHubspotMappingManager'
  )
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const { cloudType: routeCloudType, accountId: routeAccountId } = useParams<{
    cloudType?: string
    accountId?: string
  }>()
  const marketplaceCloudAccounts = useCloudAccounts('flyout')
  const resolvedCloud = (
    routeCloudType || selectedCloudTypeProductListing
  ).toUpperCase() as CloudType
  const selectedAccountId = useAccountId({
    product: 'flyout',
    cloud: resolvedCloud,
  })
  const accountId = routeAccountId || selectedAccountId
  const cloudType = useCloud({ product: 'flyout', accountId })
  const partnerUserId = useSelector<string | undefined>(
    state => state.userProfile.userProfile.id
  ) as string
  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.CREATE_REFERRAL_TEMPLATE)
  )
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [templateName, setTemplateName] = useState<string>('')
  const [templateNameError, setTemplateNameError] = useState<string>('')
  const [acceptDisabled, setAcceptDisabled] = useState<boolean>(true)
  const [selectedProductId, setSelectedProductId] = useState<string>('')
  const [offerType, setOfferType] = useState<string>(
    FlyoutFunction.PRIVATE_OFFERS
  )
  const [selectedModalAccountId, setSelectedModalAccountId] = useState('')
  const selectedModalAccount = marketplaceCloudAccounts.find(
    account => account.id === selectedModalAccountId
  )

  const products = useSelector<ProductsWithDimensions[]>(
    state => state.marketplaceMappingManager.productsWithDimensions
  )

  const privateOfferDimensions = useMemo(() => {
    return products?.map(productItem => ({
      productId: productItem?.productId,
      productName: productItem?.productName,
      dimensions: productItem?.productDimensions?.map(dimensionItem => ({
        dimensionId: dimensionItem?.id,
        dimensionName: dimensionItem?.label || dimensionItem?.name,
      })),
    }))
  }, [products])

  useEffect(() => {
    if (accountId && cloudType) {
      dispatch(
        getMarketplaceProductsWithDimensions(
          accountId,
          cloudType.toUpperCase() as CloudMarketplace
        )
      )
    }
  }, [accountId, cloudType])

  const handleButtonClick = () => {
    setSelectedModalAccountId(
      getSelectedAccountIdWithCloud(accountId, 'flyout', resolvedCloud)
    )
    setOpenModal(true)
  }
  const handleCloseCreateModal = () => {
    setOpenModal(false)
    setTemplateName('')
    setTemplateNameError('')
    setAcceptDisabled(true)
    setOfferType(FlyoutFunction.PRIVATE_OFFERS)
    setSelectedModalAccountId('')
  }

  const handleOfferTypeChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value as FlyoutFunction

    if (!value || value === offerType) {
      return
    } else {
      setOfferType(value)
      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )
    }
  }

  const handleTemplateNameBlur = () => {
    if (!templateName.trim()) {
      setTemplateNameError('Template name is required.')
      setAcceptDisabled(true)
    }
  }

  const handleTemplateNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setTemplateName(value)
    if (!value.trim()) {
      setTemplateNameError('Template name is required.')
      setAcceptDisabled(true)
    } else {
      setTemplateNameError('')
      setAcceptDisabled(false)
    }
  }

  const handleCreateTemplate = async () => {
    const trimmedName = templateName.trim()
    if (!trimmedName) {
      setTemplateNameError('Template name is required.')
      return
    }
    if (
      crmType === CRMTypes.hubspot &&
      resolvedCloud === CloudType.AZURE &&
      (!isAzureHubspotMappingManagerEnabled || !selectedModalAccount)
    ) {
      return
    }
    const referralTemplatePayload = {
      name: trimmedName,
      templateType: 'flyout',
      createdBy: partnerUserId,
      ...(crmType === CRMTypes.hubspot
        ? {
            cloudProvider: (
              selectedModalAccount?.provider || resolvedCloud
            ).toUpperCase(),
            accountId: selectedModalAccount?.originalId || '',
            offerType,
          }
        : {}),
    }

    const shouldUseSalesforceTemplateCreation =
      isSalesforceMappingManagerEnabled &&
      crmType === CRMTypes.salesforce &&
      accountId

    const shouldUseAzureHubspotMappingService =
      isAzureHubspotMappingManagerEnabled &&
      crmType === CRMTypes.hubspot &&
      selectedModalAccount?.provider?.toUpperCase() === CloudType.AZURE
    const mappingServiceAccountId = shouldUseAzureHubspotMappingService
      ? selectedModalAccount.originalId
      : accountId
    if (
      shouldUseAzureHubspotMappingService &&
      (!crmId || !mappingServiceAccountId)
    ) {
      setTemplateNameError(
        'Connect a CRM and Azure account before creating a template.'
      )
      return
    }
    const creationCloud = shouldUseAzureHubspotMappingService
      ? CloudType.AZURE
      : cloudType
    const cloudTypeLower = creationCloud?.toLowerCase?.() ?? ''
    const isPrivateOffer = offerType === FlyoutFunction.PRIVATE_OFFERS

    const salesforceTemplatePayload: Record<string, unknown> = {
      cloudProvider: shouldUseAzureHubspotMappingService
        ? CloudType.AZURE
        : cloudType,
      createdBy: partnerUserId,
      isDraft: false,
      name: trimmedName,
      crmType,
      productType: 'marketplace',
      templateType: isPrivateOffer ? 'private_offer' : 'resale_auth',
      ...(cloudTypeLower === CloudType.AWS.toLowerCase()
        ? {
            awsPrivateOfferDimensions: privateOfferDimensions,
          }
        : {}),
      ...(cloudTypeLower === CloudType.AZURE.toLowerCase()
        ? {
            azurePrivateOfferDimensions: privateOfferDimensions,
          }
        : {}),
    }

    const { templateId, errorMessage } = (await dispatch(
      shouldUseSalesforceTemplateCreation || shouldUseAzureHubspotMappingService
        ? createMarketplaceSalesforceTemplate(
            salesforceTemplatePayload,
            crmId,
            mappingServiceAccountId,
            true
          )
        : createMarketplaceReferralTemplate(referralTemplatePayload, crmId)
    )) as unknown as { templateId: string; errorMessage: string }

    if (!isEmpty(templateId)) {
      setOpenModal(false)
      if (
        (shouldUseSalesforceTemplateCreation ||
          shouldUseAzureHubspotMappingService) &&
        mappingServiceAccountId &&
        creationCloud
      ) {
        navigate(
          `/settings/${creationCloud.toLowerCase()}/${mappingServiceAccountId}/mappings/marketplace/templates/${templateId}`
        )
      } else {
        navigate(`/settings/mappings/marketplace/templates/${templateId}`)
      }
      await dispatch(
        updateAppAlert({
          message: `${templateName} created successfully.`,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      setTemplateName('')
      setTemplateNameError('')
    } else {
      const duplicateError = 'Template name already exists'
      setTemplateNameError(
        errorMessage && errorMessage.includes(duplicateError)
          ? 'Template already exists. Please enter a unique name.'
          : errorMessage || 'Failed to create template. Please try again.'
      )
      setOpenModal(true)
    }
  }
  const handleProductChangeLocal = useCallback(
    (productId: string) => {
      setSelectedProductId(productId)
      handleProductChange ? handleProductChange(productId) : null
    },
    [selectedProductId]
  )
  const offerSelectionMapping: RadioButtonGroupMapping[] = [
    {
      key: FlyoutFunction.PRIVATE_OFFERS,
      label: 'Offer',
      type: RadioButtonType.STANDARD,
    },
    {
      key: FlyoutFunction.RESALE_AUTHORIZATIONS,
      label: 'Reseller authorization',
      type: RadioButtonType.STANDARD,
    },
  ]

  return (
    <>
      <ButtonV2
        data-testid="create-new-template-button"
        styleType="primary"
        onClick={handleButtonClick}
        disabled={buttonDisable}
      >
        Create new template
      </ButtonV2>
      <ModalConfirmV2
        title="Create a new template"
        icon={
          <FontAwesomeIcon
            icon={faPlus}
            data-testid="create-new-template-icon-modal"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseCreateModal}
        onDecline={handleCloseCreateModal}
        content={
          <div className={classes.modalWpapper}>
            <div className={classes.textInputContainer}>
              <TextInputV4
                keyName="templateName"
                title="Enter a unique template name"
                placeHolder=""
                type="text"
                maxCharacters={crmType === CRMTypes.hubspot ? 26 : undefined}
                startAdornment={
                  crmType === CRMTypes.hubspot ? (
                    <span className={classes.templateNameCloudIcon}>
                      <SelectedCloudIcon />
                    </span>
                  ) : undefined
                }
                value={templateName}
                updatedValue={templateName}
                onBlur={handleTemplateNameBlur}
                onChange={handleTemplateNameChange}
                isError={!!templateNameError}
                errorText={templateNameError}
                isReadOnly={false}
                isRequired={true}
                showSpinner={false}
                helperText={true}
                supportingText={
                  !templateName ? (
                    <div className={classes.exampleText}>
                      Example: Marketplace template
                    </div>
                  ) : null
                }
              />
            </div>

            {(crmType === CRMTypes.salesforce ||
              crmType === CRMTypes.hubspot) && (
              <div className={classes.salesforceContainer}>
                {(handleCloudAccountChange || crmType === CRMTypes.hubspot) && (
                  <CloudAccountChangeV2
                    cloudAccounts={getCloudAccountsForMappingManager(
                      crmType === CRMTypes.hubspot
                        ? marketplaceCloudAccounts.filter(
                            account => account.provider === resolvedCloud
                          )
                        : marketplaceCloudAccounts,
                      [
                        CloudType.GCP,
                        ...((
                          crmType === CRMTypes.hubspot
                            ? isAzureHubspotMappingManagerEnabled
                            : isAzureMarketplaceMappingManagerEnabled
                        )
                          ? []
                          : [CloudType.AZURE]),
                      ]
                    )}
                    selectedAccountId={
                      crmType === CRMTypes.hubspot
                        ? selectedModalAccountId
                        : (selectedAccountIdWithCloud as string)
                    }
                    onChange={
                      crmType === CRMTypes.hubspot
                        ? ({ accountId }) =>
                            setSelectedModalAccountId(accountId)
                        : handleCloudAccountChange!
                    }
                    width={220}
                    size="small"
                  />
                )}
                <div className={classes.radioButtonContainer}>
                  <Typography className={classes.infoText}>
                    Select template type
                  </Typography>
                  <div
                    data-testid="offer-selection-options"
                    className={classes.radioWithRadio}
                  >
                    <RadioButtonsV2
                      radioButtonMappings={offerSelectionMapping}
                      checkedValue={{
                        key: offerType,
                        value: offerType,
                      }}
                      handleChange={handleOfferTypeChange}
                      gapBetween="24px"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        }
        open={openModal}
        titleCancelButton={'Cancel'}
        titleAcceptButton={'Create'}
        onAccept={handleCreateTemplate}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptDisabled={
          acceptDisabled ||
          (crmType === CRMTypes.hubspot &&
            resolvedCloud === CloudType.AZURE &&
            (!isAzureHubspotMappingManagerEnabled || !selectedModalAccount))
        }
        acceptLoading={isLoading}
      />
    </>
  )
}
